import { z } from "zod";

// Enums from Prisma (replicated for the widget)
export enum BookingType {
  INSTANT = "INSTANT",
  REQUEST = "REQUEST",
}

export enum BookinLimitType {
  CAPACITY = "CAPACITY",
  SLOT = "SLOT",
}


export enum DurationType {
  MINUTE = "MINUTE",
  HOUR = "HOUR",
  DAY = "DAY",
}

export enum GiftValidationType {
  THREE_MONTHS = "THREE_MONTHS",
  SIX_MONTHS = "SIX_MONTHS",
  ONE_YEAR = "ONE_YEAR",
}

export enum ProductLanguage {
  FR = "FR",
  EN = "EN",
  ES = "ES",
  DE = "DE",
  IT = "IT",
}

export enum TvaType {
  TVA_0 = "TVA_0",
  TVA_2_1 = "TVA_2_1",
  TVA_5_5 = "TVA_5_5",
  TVA_10 = "TVA_10",
  TVA_20 = "TVA_20",
}

export const ProductImageFormSchema = z.object({
  id: z.string().nullish(),
  position: z.number(),
  url: z.string(),
  title: z.string().nullish(),
  caption: z.string().nullish(),
});

export const ProductScheduleFormSchema = z.object({
  id: z.string().nullish(),
  type: z.string(),
  startHour: z.string(),
  endHour: z.string().optional(),
});

export const ProductTimeSlotFormSchema = z.object({
  id: z.string().nullish(),
  active: z.boolean(),
  dayNumber: z.number(),
  maxCapacity: z.coerce.number().nullish(),
  schedules: z.array(ProductScheduleFormSchema),
});

export const ProductAvailabilityFormSchema = z
  .object({
    id: z.string().nullish(),
    startDate: z.coerce.date().nullable(),
    endDate: z.coerce.date().nullable(),
    timeSlots: z.array(ProductTimeSlotFormSchema),
  })
  .superRefine((data, ctx) => {
    if (!data.startDate && !data.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Une date de début ou de fin est requise",
        fatal: true,
        path: ["endDate"],
      });

      return z.NEVER;
    }

    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin doit être supérieure à la date de début",
        fatal: true,
        path: ["endDate"],
      });
    }
  });

export const ProductUnitFormSchema = z.object({
  id: z.string().nullish(),
  name: z.string().min(1, "Type de billet ne doit pas être vide"),
  condition: z.string().nullish(),
  price: z.coerce.number(),
  tva: z.enum(TvaType),
  promotionPrice: z.coerce.number().nullish(),
  currency: z.string(),
  duration: z.coerce.number(),
  durationType: z.enum(DurationType),
});

export const ProductClosureDateFormSchema = z.object({
  id: z.string().nullish(),
  date: z.coerce.date(),
});

export const ProductOptionFormSchema = z.object({
  id: z.string().nullish(),
  slug: z.string(),
  name: z.string().min(1, "Le nom de la prestation ne doit pas être vide"),
  units: z.array(ProductUnitFormSchema).min(1, "Un prix est requis"),
  availabilities: z
    .array(ProductAvailabilityFormSchema)
    .min(1, "Une plage de disponibilité est requise"),
  closureDates: z.array(ProductClosureDateFormSchema).default([]),
  additionalFields: z
    .array(z.string().min(1, "Le nom du champ ne peut pas être vide"))
    .default([]),
  type: z.enum(BookingType),
  limit: z.enum(BookinLimitType),
  cancellationPolicy: z.string().default("Non échangeable, non remboursable"),
});

export const ProductSelectFormSchema = z
  .object({
    id: z.string().nullish(),
    slug: z.string(),
    active: z.boolean().optional(),
    title: z.string().min(1, "Le titre de la prestation ne doit pas être vide"),
    description: z
      .string()
      .min(1, "La description d'introduction ne doit pas être vide"),
    shortDescription: z
      .string()
      .min(1, "Le texte introductif ne doit pas être vide"),
    images: z.array(ProductImageFormSchema).min(1, "Une image est requise"),
    options: z.array(ProductOptionFormSchema).min(1, "Une option est requise"),
    organization: z.object({
      id: z.string(),
      cgvFile: z.string().nullish(),
      stripeConnectedLinked: z.boolean().optional(),
      stripePublishableKey: z.string().optional(),
    }),
    highlights: z.array(z.string()),
    inclusions: z.array(z.string()),
    exclusions: z.array(z.string()),
    additionalInfos: z.array(z.string()),
    languages: z.array(z.enum(ProductLanguage)),
    isGift: z.boolean().default(false),
    giftValidationType: z.enum(GiftValidationType).nullable(),
    address: z.object({
      id: z.string().nullish(),
      placeId: z.string(),
      street_number: z.string().nullish(),
      route: z.string(),
      city: z.string(),
      country: z.string(),
      postalCode: z.coerce.number(),
      latitude: z.coerce.number(),
      longitude: z.coerce.number(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.isGift && !data.giftValidationType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La durée de validité est requise pour un bon cadeau",
        path: ["giftValidationType"],
      });
    }
  });

export type ProductSelectFormSchemaType = z.infer<
  typeof ProductSelectFormSchema
>;

export type ProductImageFormSchemaType = z.infer<typeof ProductImageFormSchema>;

export type ProductOptionFormSchemaType = z.infer<
  typeof ProductOptionFormSchema
>;

export type ProductUnitFormSchemaType = z.infer<typeof ProductUnitFormSchema>;

export type ProductAvailabilityFormSchemaType = z.infer<
  typeof ProductAvailabilityFormSchema
>;

export type ProductTimeSlotFormSchemaType = z.infer<
  typeof ProductTimeSlotFormSchema
>;

export type ProductScheduleFormSchemaType = z.infer<
  typeof ProductScheduleFormSchema
>;

export type ProductClosureDateFormSchemaType = z.infer<
  typeof ProductClosureDateFormSchema
>;
